import * as react from 'react';
import * as api from '@opentelemetry/api';
import {Context, ROOT_CONTEXT, Span, SpanOptions} from '@opentelemetry/api';
import {StackContextManager} from '@opentelemetry/sdk-trace-web';

const _tracer = api.trace.getTracer(`web-client`);

export enum AppLevelSpanIds {
  BOARDS = `boards-app`,
  INSIGHTS = `insights-app`,
  LEXICON = `lexicon-app`,
}

class SpanTreeManager {
  private spans: Map<string, Span> = new Map<string, Span>();
  private appLevelSpanId: string = ``;

  /**
   * This function either returns an existing span if present or creates a new one, if not
   * @param span.id         The id for the requested span
   * @param span.name?      The name for the requested span (defaults to id, if not provided)
   * @param span.options?   A SpanOptions object to specify additional options for span creation
   * @param span.parentId?  The id of the parent span (defaults to the spanId of the current app level span, if not provided)
   * @returns an array consisting of the requested span and a boolean indicating whether the span was newly created
   */
  getOrCreateSpan(span: {id: string; name?: string; options?: SpanOptions; parentId?: string}): [Span, boolean] {
    const name = span.name ? span.name : span.id;
    const parentId = span.parentId ? span.parentId : this.appLevelSpanId;
    const existingSpan = this.getSpanById(span.id);
    if (existingSpan) {
      return [existingSpan, false];
    }

    const newSpan = this.withSpanById(parentId, () => {
      return _tracer.startSpan(name, span.options);
    });
    this.saveSpanById(span.id, newSpan);
    return [newSpan, true];
  }

  /**
   * This function sets and returns either an existing app level span if present or creates a new one, if not
   * @param id        The id for the requested span
   * @param options?  A SpanOptions object to specify additional options for span creation
   * @returns an array consisting of the requested span and a boolean indicating whether the span was newly created
   */
  getOrCreateAppLevelSpan(id: AppLevelSpanIds, options?: SpanOptions): [Span, boolean] {
    // End existing app-level span, if any
    if (this.appLevelSpanId) {
      this.endSpanById(this.appLevelSpanId);
    }

    // Return existing span with id, if any (and make it global)
    this.appLevelSpanId = id.toString();
    const span = this.getSpanById(this.appLevelSpanId);
    if (span) {
      this.makeSpanGlobal(span);
      return [span, false];
    }

    // Create new global span with given id
    const newSpan = _tracer.startSpan(id, options, ROOT_CONTEXT);
    this.makeSpanGlobal(newSpan);
    this.saveSpanById(this.appLevelSpanId, newSpan);
    return [newSpan, true];
  }

  getAppLevelSpanId(): string {
    return this.appLevelSpanId;
  }

  makeSpanGlobal(span: Span) {
    const spanContext = api.trace.setSpan(api.context.active(), span);
    api.context.bind(spanContext, null);
  }

  saveSpanById(id: string, span: Span): void {
    this.spans.set(id, span);
  }

  getSpanById(id: string): Span | undefined {
    return this.spans.get(id);
  }

  setAttributesById(id: string, attributes: api.Attributes): Span | null {
    const span = this.spans.get(id);
    if (!span) {
      return null;
    }
    return span.setAttributes(attributes);
  }

  setErrorById(id: string, message?: string): Span | null {
    const span = this.spans.get(id);
    if (!span) {
      return null;
    }
    return span.setStatus({
      code: api.SpanStatusCode.ERROR,
      message,
    });
  }

  endSpanById(id: string, endTime?: api.TimeInput): boolean {
    this.spans.get(id)?.end(endTime);
    if (this.appLevelSpanId === id) {
      this.appLevelSpanId = ``;
    }
    return this.spans.delete(id);
  }

  endAppLevelSpan(): boolean {
    return this.endSpanById(this.appLevelSpanId);
  }

  withSpanById<A extends unknown[], F extends (...args: A) => ReturnType<F>>(
    id: string,
    fn: F,
    thisArg?: ThisParameterType<F>,
    ...args: A
  ): ReturnType<F> {
    const span = this.getSpanById(id);
    const spanContext = span ? api.trace.setSpan(api.context.active(), span) : api.context.active();

    return api.context.with(spanContext, fn, thisArg, ...args);
  }
}

export const spans = new SpanTreeManager();

export class ImperativeContextManager extends StackContextManager {
  private _contextStack: api.Context[] = [];

  override active() {
    let span = api.trace.getSpan(this._currentContext);
    while (span?.isRecording() === false) {
      this._contextStack.pop();
      this._currentContext = this._contextStack[this._contextStack.length - 1] || ROOT_CONTEXT;
      span = api.trace.getSpan(this._currentContext);
    }
    return super.active();
  }

  override bind<T>(context: Context, target: T): T {
    this._contextStack.push(context);
    this._currentContext = context;
    return super.bind(context, target);
  }

  override with<A extends unknown[], F extends (...args: A) => ReturnType<F>>(
    context: Context | null,
    fn: F,
    thisArg?: ThisParameterType<F>,
    ...args: A
  ): ReturnType<F> {
    const thisContext = context || ROOT_CONTEXT;
    this._contextStack.push(thisContext);
    this._currentContext = thisContext;

    try {
      return fn.call(thisArg, ...args);
    } finally {
      this._contextStack.pop();
      this._currentContext = this._contextStack[this._contextStack.length - 1] || ROOT_CONTEXT;
    }
  }
}

export const useTracing = () => {
  const getOrCreateSpan = react.useCallback(
    (span: {id: string; name?: string; options?: SpanOptions; parentId?: string}): [Span, boolean] => {
      return spans.getOrCreateSpan(span);
    },
    [],
  );

  const getOrCreateAppLevelSpan = react.useCallback((id: AppLevelSpanIds, options?: SpanOptions): [Span, boolean] => {
    return spans.getOrCreateAppLevelSpan(id, options);
  }, []);

  const setAttributes = react.useCallback((name: string, attributes: api.Attributes): Span | null => {
    return spans.setAttributesById(name, attributes);
  }, []);

  const setError = react.useCallback((name: string, message: string): Span | null => {
    return spans.setErrorById(name, message);
  }, []);

  const endSpan = react.useCallback((id: string) => {
    spans.endSpanById(id);
  }, []);

  return {
    getOrCreateSpan,
    getOrCreateAppLevelSpan,
    setAttributes,
    setError,
    endSpan,
  };
};
